<template>
  <PageContainer :class="$style.container">
    <template v-slot:body>
      <ApplicationItemList
        v-if="renderItemListSection"
        v-show="showItemListSection"
        :data-test-id="$testId('itemListSection')"
      />
      <ApplicationItemListEmptyState
        v-if="showEmptyStateSection"
      />
    </template>
  </PageContainer>
</template>

<script>
import { PageContainer } from '@jumpcloud/ui-components';
import { mapGetters } from 'vuex';

import { FilterComponentConsts } from 'client/util/Constants';

import ApplicationItemList from 'client/components/ItemTable/ApplicationItemList.vue';
import ApplicationItemListEmptyState from 'client/components/ItemTable/ApplicationItemListEmptyState.vue';
import MainPanelMixin from 'components/MainPanel/mixins/MainPanelMixin';

const { ApplicationsDropDownFilter } = FilterComponentConsts;

export default {
  name: 'ApplicationMainPanel',

  components: {
    ApplicationItemList,
    ApplicationItemListEmptyState,
    PageContainer,
  },

  mixins: [MainPanelMixin],

  data: () => ({
    collectionLoaded: false,
    collectionHasItems: false,
  }),

  ApplicationsDropDownFilter,

  computed: {
    ...mapGetters('ApplicationCollection', {
      applicationsTotalCount: 'getTotalCountOfAllItems',
    }),

    /**
     * Initially, we want to render the ItemList so <ItemListPagination />
     * can fetch ApplicationCollection, which will load `getTotalCountOfAllItems`
     * into state.
     *
     * Once we know if there are items to display or not, we can choose to:
     * 1. keep rendering (and show) the ItemList (if there are items to display)
     * OR...
     * 2. stop rendering the ItemList (if there are no items to display)
     */
    renderItemListSection() {
      return !this.collectionLoaded || this.showItemListSection;
    },

    /**
     * Once we've received a number value for `getTotalCountOfAllItems`, and
     * it is 0, we know there are no items to display, so we render the
     * empty state section.
     */
    showEmptyStateSection() {
      return this.collectionLoaded && !this.collectionHasItems;
    },

    /**
     * When the collection finishes loading, we can finally tell if we want to
     * "switch on" the ItemList (show it instead of continuing to hiding it).
     *
     * The combination of this property and `this.renderItemListSection`
     * allows us to render, but conditionally show or hide, the ItemList.
     */
    showItemListSection() {
      return this.collectionLoaded && this.collectionHasItems;
    },
  },

  watch: {
    /**
     * There is a small delay between the render of this component
     * and the render of <ItemListPagination /> (grandchild component).
     *
     * <ItemListPagination /> calls `fetchCollection` on its mount, which fills
     * the Vuex collection (including the getter `getTotalCountOfAllItems`).
     *
     * This means that there is a small amount of time where `getTotalCountOfAllItems`
     * is undefined when this component first mounts. So once the value updates to a number,
     * we know we can decide which view to render (see `this.renderItemListSection` for more info).
     */
    applicationsTotalCount(newTotalCount) {
      if (typeof newTotalCount === 'number') {
        if (!this.collectionHasLoaded) {
          this.collectionLoaded = true;
        }

        this.collectionHasItems = newTotalCount > 0;
      }
    },
  },
};
</script>

<style scoped module>
.container {
  height: 100%;
  overflow-y: hidden;
}
</style>
